<template>
  <div class="file-cover-report" dir="ltr">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("File Covers") }}</h4>
        <b-badge
          v-if="!loading && staffItems.length"
          variant="light-primary"
          class="ml-2"
        >
          {{ staffItems.length }} {{ $t("Staff") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print File Covers") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel">
      <b-spinner variant="primary" class="mb-1" />
      <h5 class="state-panel__title">{{ $t("Loading") }}...</h5>
      <p class="state-panel__text">
        {{ $t("Please wait while we prepare the file covers") }}
      </p>
    </div>

    <div v-else-if="error" class="state-panel">
      <feather-icon
        icon="AlertCircleIcon"
        size="36"
        class="text-danger mb-1"
      />
      <h5 class="state-panel__title text-danger">
        {{ $t("Unable to load file covers") }}
      </h5>
      <p class="state-panel__text">{{ error }}</p>
      <button class="btn-modern btn-print mt-2" @click="loadReportData">
        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
        <span>{{ $t("Retry") }}</span>
      </button>
    </div>

    <div v-else class="file-cover-stack">
      <div
        v-for="(staff, index) in staffItems"
        :key="'file-cover-' + (staff.id || index)"
        class="file-cover-page"
      >
        <div class="file-cover-header">
          <div class="file-cover-header__left">
            <span class="file-cover-header__square"></span>
            <span class="file-cover-header__name">{{ staffName(staff) }}</span>
          </div>
          <span class="file-cover-header__date">{{ joiningDate(staff) }}</span>
        </div>

        <div class="file-cover-content">
          <div class="file-cover-content__inner">
            <div class="file-cover-content__name">{{ staffName(staff) }}</div>
            <div class="file-cover-content__designation">
              ({{ staffDesignation(staff) }})
            </div>
            <div class="file-cover-content__id">
              ID-{{ staffDisplayId(staff) }}
            </div>
            <div class="file-cover-content__department">
              {{ staffDepartment(staff) }}
            </div>

            <div v-if="showFileCoverBarcode" class="file-cover-barcode">
              <div class="file-cover-barcode__bars">
                *{{ barcodeValue(staff) }}*
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

/** Barcode value is the staff ID with a trailing "+" (e.g. staff id 10 → "10+"). */
const STAFF_BARCODE_SUFFIX = "+";

/** Barcode file covers for these report domains. */
const BARCODE_FILE_COVER_DOMAINS = ["myskoolstjacob", "myskool"];

export default {
  name: "FileCover",
  components: {
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      staffItems: [],
      departments: [],
      db: "",
      cID: 0,
      staffIDs: [],
      departmentIDs: [],
      status: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    showFileCoverBarcode() {
      return BARCODE_FILE_COVER_DOMAINS.includes(this.reportDomain);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.staffIDs = String(query.staffIDs || query.staffID || "")
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id) && id > 0);

      // Reports page passes filters instead of a selection (depID=0 → all)
      const depRaw = query.depID ?? query.depIDs ?? query.dptID;
      this.departmentIDs = String(depRaw ?? "")
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      this.status = query.status || "present";

      if (!this.db) {
        this.error = this.$t(
          "System context missing. Please open this report from the staff grid."
        );
        return;
      }
      if (!this.staffIDs.length && !this.departmentIDs.length) {
        this.error = this.$t(
          "No staff or department selected for file covers."
        );
        return;
      }

      this.loadReportData();
    },

    async loadDepartments() {
      try {
        const res = await axios.get(
          `${this.$store.state.domain}StaffDepartments`,
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data && res.data.data)
          ? res.data.data
          : [];
        this.departments = list;
      } catch (e) {
        console.warn("Departments could not be loaded:", e);
      }
    },

    async loadReportData() {
      this.loading = true;
      this.error = null;
      try {
        await this.loadDepartments();

        const rows = this.staffIDs.length
          ? await this.loadSelectedStaff()
          : await this.loadStaffByDepartments();

        this.staffItems = rows;
        if (!rows.length) {
          this.error = this.$t("No staff records found for file covers.");
        }
      } catch (e) {
        console.error("File cover load error:", e);
        this.error = this.$t("Failed to load staff records.");
      } finally {
        this.loading = false;
      }
    },

    extractRows(payload) {
      if (payload && payload.data && Array.isArray(payload.data.data)) {
        return payload.data.data;
      }
      if (payload && Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload)) return payload;
      if (payload && payload.data && typeof payload.data === "object") {
        return [payload.data];
      }
      return [];
    },

    async loadSelectedStaff() {
      const res = await axios.get(
        `${this.$store.state.domain}Staff/LoadDataSelected`,
        {
          params: {
            db: this.db,
            cID: this.cID,
            tIDs: this.staffIDs.join(","),
          },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        }
      );
      return this.extractRows(res.data);
    },

    async loadStaffByDepartments() {
      const depIDs = this.departmentIDs.length ? this.departmentIDs : [0];
      const byId = new Map();

      for (const depID of depIDs) {
        const res = await axios.get(
          `${this.$store.state.domain}staff/LoadDataPagination`,
          {
            params: {
              db: this.db,
              cId: this.cID,
              dptID: depID,
              status: this.status || "present",
              depname: "",
              page: 1,
              pageSize: 10000,
              sortBy: "rfid",
              sortOrder: "asc",
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        this.extractRows(res.data).forEach((row) => {
          const key =
            row && row.id !== undefined && row.id !== null
              ? String(row.id)
              : `${depID}-${byId.size}`;
          byId.set(key, row);
        });
      }

      return [...byId.values()];
    },

    staffName(staff) {
      return String(
        staff.staffName || staff.name || staff.fullName || "N/A"
      ).trim();
    },

    staffDesignation(staff) {
      return String(staff.designation || staff.position || "N/A").trim();
    },

    staffDisplayId(staff) {
      return (
        staff.rfID || staff.rfid || staff.RFID || staff.id || "N/A"
      );
    },

    staffDepartment(staff) {
      if (!staff) return "N/A";
      if (staff.departmentName) return staff.departmentName;
      if (typeof staff.department === "string" && staff.department.trim()) {
        return staff.department.trim();
      }
      if (staff.department && staff.department.name) {
        return staff.department.name;
      }
      const depId = Number(staff.departmentID);
      if (!depId) return "N/A";
      const match = this.departments.find((dep) => Number(dep.id) === depId);
      return match && match.name ? match.name : "N/A";
    },

    barcodeValue(staff) {
      const rawId =
        staff.id !== null && staff.id !== undefined && staff.id !== ""
          ? staff.id
          : staff.rfID;
      return `${String(rawId || "").trim()}${STAFF_BARCODE_SUFFIX}`;
    },

    joiningDate(staff) {
      return this.formatDate(
        staff.appointedOn ||
          staff.joiningDate ||
          staff.appointmentDate ||
          staff.createdAt
      );
    },

    formatDate(value) {
      if (!value || value === "undefined" || value === "null") return "N/A";
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "N/A";
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (e) {
        return "N/A";
      }
    },

    print() {
      window.print();
    },

    close() {
      // Opened in its own tab from the staff grid
      window.close();
      this.$router.push({ name: "manage-staffgrid" });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap");

$brand-blue: #0066cc;

.file-cover-report {
  min-height: 100vh;
  padding: 2rem;
  background: #f8f9fa;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  /* Keep the cover LTR/centered even when the app is in RTL mode */
  direction: ltr;
  text-align: left;
}

.no-print.control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-radius: 10px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h4 {
    font-weight: 700;
    color: #444;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-print {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.state-panel {
  max-width: 420px;
  margin: 3rem auto;
  padding: 2rem;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  &__title {
    font-weight: 700;
    margin-bottom: 0.35rem;
  }

  &__text {
    color: #636e72;
    margin: 0;
  }
}

.file-cover-stack {
  display: block;
}

.file-cover-page {
  page-break-after: always;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  direction: ltr;
  text-align: left;
}

.file-cover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 900px;
  margin: 0 auto 60px;
  padding: 20px;
  background: #fff;
  border: 2px solid $brand-blue;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 102, 204, 0.1);
}

.file-cover-header__left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.file-cover-header__square {
  width: 16px;
  height: 16px;
  margin-right: 12px;
  border-radius: 2px;
  background: $brand-blue;
  flex-shrink: 0;
}

.file-cover-header__name {
  font-size: 18px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  word-break: break-word;
}

.file-cover-header__date {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  white-space: nowrap;
  margin-left: 16px;
}

.file-cover-content {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
  padding: 8px;
  background: #fff;
  border: 3px solid $brand-blue;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 102, 204, 0.1);
  text-align: center;
}

.file-cover-content__inner {
  padding: 35px 25px;
  background: #fff;
  border: 2px solid $brand-blue;
  border-radius: 12px;
}

.file-cover-content__name {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 20px;
  word-break: break-word;
}

.file-cover-content__designation {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 15px;
}

.file-cover-content__id {
  font-size: 22px;
  font-weight: 700;
  color: #000;
  margin-bottom: 15px;
}

.file-cover-content__department {
  font-size: 20px;
  font-weight: 700;
  text-transform: uppercase;
  color: #000;
}

.file-cover-barcode {
  margin-top: 22px;
  text-align: center;
}

.file-cover-barcode__bars {
  font-family: "Libre Barcode 39", cursive;
  font-size: 42px;
  line-height: 0.8;
  color: #000;
  letter-spacing: 1px;
  white-space: nowrap;
}

@page {
  size: A4 portrait;
  margin: 12mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  .file-cover-report {
    background: #fff;
    padding: 0;
    min-height: auto;
  }

  .file-cover-page {
    page-break-after: always;
    break-after: page;
    margin: 0;
    max-width: none;

    &:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  }

  .file-cover-header,
  .file-cover-content,
  .file-cover-content__inner {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
